import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import LoginForm from '../components/LoginForm';       // your login form component
import CategoryEditor from '../components/CategoryEditor'; // stub or your real one
import SlideEditor from '../components/SlideEditor';
import PageEditor from '../components/PageEditor';
import styled from 'styled-components';

const AdminContainer = styled.div`
  padding: 20px;
`;

const Section = styled.section`
  margin-top: 32px;
  padding: 16px;
  border: 1px solid #444;
  border-radius: 8px;
  background: #111;
`;

const SectionTitle = styled.h2`
  color: #e1d6c0;
  margin-bottom: 12px;
  font-size: 1.5rem;
`;

const BannerEditorWrap = styled.div`
  color: #e1d6c0;
  display: grid;
  gap: 12px;

  label {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  textarea {
    min-height: 86px;
    padding: 10px;
    border: 1px solid #666;
    border-radius: 4px;
    background: #111;
    color: #fff;
    resize: vertical;
  }

  button {
    justify-self: start;
    padding: 8px 14px;
    border: 1px solid #e1d6c0;
    border-radius: 4px;
    background: transparent;
    color: #e1d6c0;
    cursor: pointer;
    &:hover { opacity: 0.8; }
  }
`;

function FrontpageBannerEditor({ token }) {
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');

  useEffect(() => {
    fetch('/api/admin/frontpage-banner', {
      headers: { Authorization: 'Bearer ' + token }
    })
      .then(r => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then(data => {
        setEnabled(!!data.enabled);
        setMessage(data.message || '');
      })
      .catch(() => setStatus('Failed to load banner settings.'));
  }, [token]);

  const save = () => {
    setStatus('Saving...');
    fetch('/api/admin/frontpage-banner', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token
      },
      body: JSON.stringify({ enabled, message })
    })
      .then(r => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then(data => {
        setEnabled(!!data.enabled);
        setMessage(data.message || '');
        setStatus('Saved.');
      })
      .catch(() => setStatus('Failed to save banner.'));
  };

  return (
    <BannerEditorWrap>
      <label>
        <input
          type="checkbox"
          checked={enabled}
          onChange={e => setEnabled(e.target.checked)}
        />
        Show closure banner on homepage
      </label>
      <textarea
        value={message}
        onChange={e => setMessage(e.target.value)}
        placeholder="Example: We will be closed on July 4 for the holiday."
      />
      <button onClick={save}>Save Banner</button>
      {status ? <div>{status}</div> : null}
    </BannerEditorWrap>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem('jwt'));
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [pages, setPages] = useState([]);
  const bump = () => setRefreshFlag(f=>!f);

   const reloadCats = () => {
       if (!token) return;
       setLoading(true);
       fetch('/api/admin/categories', {
         headers: { Authorization: 'Bearer ' + token }
       })
         .then(r => {
           if (!r.ok) throw new Error('Auth failed');
           return r.json();
         })
         .then(setCats)
         .catch(() => {
           localStorage.removeItem('jwt');
           setToken(null);
         })
         .finally(() => setLoading(false));
     };
    
     useEffect(reloadCats, [token, refreshFlag]);

  useEffect(()=> {
    if (!token) return;
    fetch('/api/admin/pages', {
      headers:{Authorization:'Bearer '+token}
    })
      .then(r=>r.json())
      .then(setPages);
  }, [token]);

  const reload = () => {
    fetch('/api/admin/pages', {
      headers:{Authorization:'Bearer '+token}
    })
      .then(r=>r.json())
      .then(setPages);
  };

  // If not logged in, show the login form
  if (!token) {
    return <LoginForm onAuth={t => {
      localStorage.setItem('jwt', t);
      setToken(t);
    }} />;
  }

  // While we’re fetching after login…
  if (loading) {
    return <p style={{ padding: 20 }}>Loading…</p>;
  }

  return (
    <AdminContainer>
      <h1 style={{ color: '#e1d6c0' }}>Admin Menu Editor</h1>
      <button onClick={() => {
        localStorage.removeItem('jwt');
        setToken(null);
      }}>Log out</button>

      <Section>
        <SectionTitle>Manage Home Page Slides</SectionTitle>
        <SlideEditor token={token} onChange={bump} />
      </Section>

      <Section>
        <SectionTitle>Homepage Closure Banner</SectionTitle>
        <FrontpageBannerEditor token={token} />
      </Section>

      <Section>
        <SectionTitle>Manage Menu Pages</SectionTitle>
        {pages.map(p => (
          <PageEditor
            key={p.id}
            page={p}
            token={token}
            onChange={reload}
          />
        ))}
        {/* new page */}
        <PageEditor page={null} token={token} onChange={reload} />
      </Section>

      <Section>
        <SectionTitle>Manage Categories</SectionTitle>
        {cats.map(cat => (
          <CategoryEditor
            key={cat.id}
            cat={cat}
            token={token}
            onChange={bump}
          />
        ))}
        {/* new category */}
        <CategoryEditor token={token} onChange={bump} />
      </Section>
    </AdminContainer>
  );
}
